/**
 * SupervisorHome — RPM main dashboard.
 *
 * Layout per `rpm-supervisor-home.html`:
 *   - Header row (title + actions)
 *   - Filter chip row (Today / branch / FSR / live polling indicator)
 *   - Alert banner (threshold tripped)
 *   - KPI strip (4 tiles — `KpiTile` primitive)
 *   - 8-widget grid (`WidgetCard`)
 *   - 300 px right rail (My Stuff / Activity)
 *   - Footer: Operator tools (Broadcast / Quick Reassign / Override)
 *
 * Polling: 60s via useEffect interval. AbortController cleanup + pause on unmount.
 * SignalR push for queue + presence is TODO (plumbing not in this wave).
 *
 * Role-gate: Supervisor+ only; FSR sees a friendly "role required" landing.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  KpiTile,
  WidgetCard,
  WidgetRow,
  Pill,
  Button,
  StaffPresenceRow,
} from '@/components/primitives';
import { useApiClient } from '@/contexts/ApiClientContext';
import { useAuth } from '@/contexts/AuthContext';
import { useConfig } from '@/contexts/ConfigContext';
import type { MetricsDashboardResponse } from '@retail-ops/api-client';
import {
  FIXTURE_KPIS,
  FIXTURE_PRESENCE,
  FIXTURE_TASKS,
  FIXTURE_APPROVALS,
} from './fixtures';
import { KpiTileDetail } from './components/KpiTileDetail';
import { OverrideDrawer } from './components/OverrideDrawer';
import { QuickReassignModal } from './components/QuickReassignModal';
import { BroadcastModal } from './components/BroadcastModal';

const POLL_INTERVAL_MS = 60_000;

export function SupervisorHome(): JSX.Element {
  const client = useApiClient();
  const { user } = useAuth();
  const { config } = useConfig();
  const navigate = useNavigate();

  const isSupervisorPlus =
    user?.primaryRole === 'Supervisor' ||
    user?.primaryRole === 'Manager' ||
    user?.primaryRole === 'Executive' ||
    user?.primaryRole === 'SuperAdmin';

  const [period, setPeriod] = useState<'today' | 'yesterday' | 'wtd'>('today');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [fsrFilter] = useState<string>('all');
  const [dashboard, setDashboard] = useState<MetricsDashboardResponse | null>(null);
  const [lastRefreshed, setLastRefreshed] = useState<Date | null>(null);
  const [bannerDismissed, setBannerDismissed] = useState<boolean>(false);
  const [railTab, setRailTab] = useState<'mine' | 'activity'>('mine');

  const [openKpi, setOpenKpi] = useState<number | null>(null);
  const [showOverride, setShowOverride] = useState<boolean>(false);
  const [showReassign, setShowReassign] = useState<boolean>(false);
  const [showBroadcast, setShowBroadcast] = useState<boolean>(false);

  const abortRef = useRef<AbortController | null>(null);

  const fetchDashboard = useCallback(async () => {
    abortRef.current?.abort();
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    try {
      const days = period === 'today' ? 1 : period === 'yesterday' ? 2 : 7;
      const req: { days: number; branch?: string } = { days };
      if (branchFilter !== 'all') req.branch = branchFilter;
      const res = await client.retail.metrics.dashboard(req, { signal: ctrl.signal });
      if (res.success) {
        setDashboard(res);
        setLastRefreshed(new Date());
      }
    } catch (err) {
      // AbortError is expected on unmount/refresh; swallow.
      if (err instanceof Error && err.name === 'AbortError') return;
      // TODO: log to telemetry when available; leave the fixture KPI strip up.
    }
  }, [client, period, branchFilter]);

  useEffect(() => {
    if (!isSupervisorPlus) return;
    void fetchDashboard();
    const id = window.setInterval(() => { void fetchDashboard(); }, POLL_INTERVAL_MS);
    return () => {
      window.clearInterval(id);
      abortRef.current?.abort();
    };
  }, [fetchDashboard, isSupervisorPlus]);

  const branchOptions = useMemo(
    () => [
      { value: 'all', label: 'All Branches' },
      ...config.branches.map((b) => ({ value: b.branchCode, label: b.branchName })),
    ],
    [config.branches],
  );

  if (!isSupervisorPlus) {
    return (
      <div className="module-page">
        <header className="module-page__header">
          <h1>RPM · Daily Ops</h1>
          <p className="module-landing__scope">
            This surface is role-gated to Supervisor / Manager / Executive. Reach out to your
            manager if you need access.
          </p>
        </header>
      </div>
    );
  }

  // KPIs: prefer live `dashboard.kpis` when present, otherwise show fixture.
  const liveKpis = dashboard ? [
    { ...FIXTURE_KPIS[0]!, value: dashboard.kpis.cartsCreated ?? FIXTURE_KPIS[0]!.value },
    { ...FIXTURE_KPIS[1]!, value: dashboard.kpis.slaBreaches ?? FIXTURE_KPIS[1]!.value },
    FIXTURE_KPIS[2]!,
    FIXTURE_KPIS[3]!,
  ] : FIXTURE_KPIS;

  return (
    <div className="module-page">
      <div className="rpm-header">
        <div className="rpm-header__top">
          <div>
            <h1>Retail Performance Metrics</h1>
            <div className="sub">Supervisor Daily Ops · Real-time polling 60s · Queue &amp; Presence push</div>
          </div>
          <div className="rpm-header__actions">
            <span className="rpm-refresh-stamp">
              {lastRefreshed ? `Last refreshed ${relative(lastRefreshed)}` : 'Loading…'}
            </span>
            <Button variant="ghost" size="sm" onClick={() => void fetchDashboard()}>⟳ Refresh</Button>
            <Button variant="primary" size="sm">⬇ Export Snapshot</Button>
          </div>
        </div>

        <div className="rpm-filter-chips" role="tablist" aria-label="RPM filters">
          <span className="rpm-chip-label">Filters:</span>
          <button
            type="button"
            role="tab"
            aria-selected={period === 'today'}
            className={`rpm-chip ${period === 'today' ? 'is-active' : ''}`.trim()}
            onClick={() => setPeriod('today')}
          >Today</button>
          <button
            type="button"
            role="tab"
            aria-selected={period === 'yesterday'}
            className={`rpm-chip ${period === 'yesterday' ? 'is-active' : ''}`.trim()}
            onClick={() => setPeriod('yesterday')}
          >Yesterday</button>
          <button
            type="button"
            role="tab"
            aria-selected={period === 'wtd'}
            className={`rpm-chip ${period === 'wtd' ? 'is-active' : ''}`.trim()}
            onClick={() => setPeriod('wtd')}
          >Week-to-date</button>
          <select
            className="rpm-chip"
            value={branchFilter}
            onChange={(e) => setBranchFilter(e.target.value)}
            aria-label="Branch filter"
          >
            {branchOptions.map((b) => (
              <option key={b.value} value={b.value}>{b.label}</option>
            ))}
          </select>
          <span className="rpm-chip is-static" aria-live="polite">● Live · 60s polling</span>
        </div>
      </div>

      {!bannerDismissed && (
        <div className="rpm-alert-banner" role="status">
          <span>
            <strong>● Threshold alert:</strong> SLA breach count reached <strong>5</strong> today
            (configured threshold: 5). Consider reassigning from Rolla DT → St. James.
          </span>
          <button
            type="button"
            className="rpm-alert-banner__close"
            aria-label="Dismiss threshold alert"
            onClick={() => setBannerDismissed(true)}
          >×</button>
        </div>
      )}

      <div className="rpm-body">
        <div className="rpm-main">
          <div className="rpm-kpi-strip">
            {liveKpis.map((kpi, i) => (
              <KpiTile
                key={kpi.label}
                label={kpi.label}
                value={kpi.value}
                severity={kpi.severity}
                delta={{ dir: kpi.deltaDir, text: kpi.deltaText }}
                drillLink={
                  <button
                    type="button"
                    className="btn btn-link"
                    style={{ fontSize: 11, fontWeight: 600 }}
                    onClick={() => setOpenKpi(i)}
                  >
                    Drill →
                  </button>
                }
                sparkline={
                  <svg viewBox="0 0 90 28" preserveAspectRatio="none" width={90} height={28} aria-hidden>
                    <polyline points={kpi.sparklinePoints} stroke={kpi.sparklineStroke} strokeWidth={2} fill="none" />
                    <polyline points={`${kpi.sparklinePoints} 90,28 0,28`} fill={kpi.sparklineFill} stroke="none" />
                  </svg>
                }
              />
            ))}
          </div>

          <div className="rpm-widget-grid">
            <WidgetCard title="Pipeline Health" stripe="pipeline" count={23} footer={<span>Source: Sessions · last 60s</span>}>
              <WidgetRow label="In flight (< 4h)" value={19} tone="green" />
              <WidgetRow label="Stalled (> 4h)" value={3} tone="amber" />
              <WidgetRow label="Abandoned (> 24h)" value={1} tone="red" />
              <WidgetRow label="Avg pipeline time" value="2h 14m" />
            </WidgetCard>

            <WidgetCard title="Queue State" stripe="queue" count="4 · 5" footer={<span>Source: RLQ · SignalR push</span>}>
              <WidgetRow label="Longest wait (lobby)" value="12 min" tone="red" />
              <WidgetRow label="Oldest unclaimed cart" value="42 min" tone="amber" />
              <WidgetRow label="Avg lobby wait" value="6m 30s" />
              <WidgetRow label="Walk-ins served" value={31} tone="green" />
            </WidgetCard>

            <WidgetCard title="SLA Watch" stripe="sla" count={5} footer={<span>Source: SlaFact</span>}>
              <WidgetRow label="Breached" value={5} tone="red" />
              <WidgetRow label="Close to breach (>80%)" value={7} tone="amber" />
              <WidgetRow label="Within window" value={41} tone="green" />
              <WidgetRow label="Compliance % today" value="89%" tone="green" />
            </WidgetCard>

            <WidgetCard title="Task Backlog" stripe="tasks" count="48 open" footer={<span>Source: TaskRecord</span>}>
              <WidgetRow label="Overdue" value={12} tone="red" miniBar={75} />
              <WidgetRow label="Due today" value={8} tone="amber" miniBar={50} />
              <WidgetRow label="CIP follow-up" value={14} />
              <WidgetRow label="Document follow-up" value={9} />
              <WidgetRow label="Appointment reminders" value={5} />
            </WidgetCard>

            <WidgetCard title="Quality & Compliance" stripe="compliance" count={11} footer={<span>Source: RCC + Dataverse</span>}>
              <WidgetRow label="SAR drafts" value={2} tone="amber" />
              <WidgetRow label="CTR triggered today" value={4} />
              <WidgetRow label="OFAC review queue" value={1} />
              <WidgetRow label="QC exceptions open" value={2} tone="red" />
              <WidgetRow label="Overall compliance" value="94%" tone="green" />
            </WidgetCard>

            <WidgetCard title="Approvals" stripe="approvals" count={7} footer={<span>Source: Teams Approvals + RAP</span>}>
              <WidgetRow label="Fee waivers" value={3} />
              <WidgetRow label="Penalty waivers" value={2} />
              <WidgetRow label="CIP exceptions" value={1} tone="amber" />
              <WidgetRow label="Limit overrides" value={1} />
              <WidgetRow label="Avg latency (pending)" value="38m" tone="amber" />
            </WidgetCard>

            <WidgetCard title="Staff Presence" stripe="staff" count="9 on" footer={<span>Source: Teams presence push</span>}>
              {FIXTURE_PRESENCE.map((p) => (
                <div key={p.initials} style={{ padding: 'var(--sp-3) 0', borderBottom: '1px solid var(--ink-100)' }}>
                  <StaffPresenceRow
                    initials={p.initials}
                    name={`${p.name} · ${p.role}`}
                    presence={p.presence}
                    metrics={`${p.claims}c · ${p.active}a`}
                    avatarColor={p.avatarColor ?? 'var(--pcb-blue)'}
                  />
                </div>
              ))}
            </WidgetCard>

            <WidgetCard title="Overrides" stripe="overrides" count="6 today" footer={<span>Audit signal · 7-day retention</span>}>
              <WidgetRow label="Fee waivers" value={3} />
              <WidgetRow label="CD penalty waivers" value={2} />
              <WidgetRow label="CIP exceptions" value={1} tone="amber" />
              <WidgetRow label="Approved by" value="AG, SB" />
              <WidgetRow label="vs yesterday" value="↑ 2" tone="green" />
            </WidgetCard>
          </div>
        </div>

        {/* Right rail */}
        <aside className="rpm-rail" aria-label="Supervisor quick panel">
          <div className="rpm-rail__toggle">
            <button
              type="button"
              className={`rpm-rail__pill ${railTab === 'mine' ? 'is-active' : ''}`.trim()}
              onClick={() => setRailTab('mine')}
            >My Stuff</button>
            <button
              type="button"
              className={`rpm-rail__pill ${railTab === 'activity' ? 'is-active' : ''}`.trim()}
              onClick={() => setRailTab('activity')}
            >Activity</button>
          </div>

          {railTab === 'mine' && (
            <>
              <section className="rpm-rail__section" aria-labelledby="rpm-day-h">
                <h3 id="rpm-day-h">My Day · Today</h3>
                <div className="rpm-day-stat-grid">
                  <div className="rpm-day-stat"><div className="rpm-day-stat__num">4</div><div className="rpm-day-stat__lbl">Opened</div></div>
                  <div className="rpm-day-stat"><div className="rpm-day-stat__num">2h 08m</div><div className="rpm-day-stat__lbl">Avg pipeline</div></div>
                  <div className="rpm-day-stat"><div className="rpm-day-stat__num">9</div><div className="rpm-day-stat__lbl">Tasks closed</div></div>
                  <div className="rpm-day-stat"><div className="rpm-day-stat__num">1</div><div className="rpm-day-stat__lbl">Approvals</div></div>
                </div>
                <div style={{ marginTop: 'var(--sp-5)', fontSize: 11, color: 'var(--ink-600)' }}>
                  <strong style={{ color: 'var(--ink-900)' }}>WTD:</strong> 18 &nbsp;·&nbsp; <strong style={{ color: 'var(--ink-900)' }}>MTD:</strong> 67
                </div>
              </section>

              <section className="rpm-rail__section" aria-labelledby="rpm-tasks-h">
                <h3 id="rpm-tasks-h">My Tasks <span className="count">{FIXTURE_TASKS.length}</span></h3>
                {FIXTURE_TASKS.map((t) => (
                  <div key={t.id} className="rpm-rail__item">
                    <span
                      className="rpm-rail__item-icon"
                      style={t.tone === 'red' ? { background: 'var(--err-bg)', color: 'var(--err-text)' } : undefined}
                    >{t.icon}</span>
                    <div className="rpm-rail__item-meta">
                      <div className="rpm-rail__item-title">{t.title}</div>
                      <div className="rpm-rail__item-sub">{t.sub}</div>
                    </div>
                    <span className={`rpm-rail__item-due ${t.overdue ? 'is-overdue' : ''}`.trim()}>{t.dueLabel}</span>
                  </div>
                ))}
              </section>

              <section className="rpm-rail__section" aria-labelledby="rpm-appr-h">
                <h3 id="rpm-appr-h">My Approvals <span className="count">{FIXTURE_APPROVALS.length}</span></h3>
                {FIXTURE_APPROVALS.map((a) => (
                  <div key={a.id} className="rpm-rail__item">
                    <span
                      className="rpm-rail__item-icon"
                      style={{ background: 'var(--info-bg)', color: 'var(--ent-application)' }}
                    >{a.icon}</span>
                    <div className="rpm-rail__item-meta">
                      <div className="rpm-rail__item-title">{a.title}</div>
                      <div className="rpm-rail__item-sub">{a.sub}</div>
                    </div>
                    <span className="rpm-rail__item-due">{a.actionLabel}</span>
                  </div>
                ))}
              </section>
            </>
          )}

          {railTab === 'activity' && (
            <section className="rpm-rail__section">
              <h3>Team activity · last 15m</h3>
              <p style={{ fontSize: 12, color: 'var(--ink-500)' }}>
                SignalR-pushed activity feed ships Wave 3.
              </p>
            </section>
          )}
        </aside>
      </div>

      <footer className="rpm-op-footer" role="toolbar" aria-label="Operator tools">
        <span className="rpm-op-label">Operator Tools</span>
        <button type="button" className="rpm-op-btn" onClick={() => setShowBroadcast(true)}>📢 Broadcast</button>
        <button type="button" className="rpm-op-btn" onClick={() => setShowReassign(true)}>⇄ Quick Reassign</button>
        <button type="button" className="rpm-op-btn is-primary" onClick={() => setShowOverride(true)}>✓ Override Authority</button>
        <span style={{ flex: 1 }} />
        <span style={{ fontSize: 10, opacity: 0.5 }}>Pipeline Emergency Stop → Admin deep-menu (compliance only)</span>
      </footer>

      <KpiTileDetail
        open={openKpi !== null}
        title={liveKpis[openKpi ?? 0]?.label ?? ''}
        value={liveKpis[openKpi ?? 0]?.value ?? ''}
        subtitle={`${period} · ${branchFilter === 'all' ? 'All branches' : branchFilter} · ${fsrFilter === 'all' ? 'All FSRs' : fsrFilter}`}
        onClose={() => setOpenKpi(null)}
      >
        <p style={{ fontSize: 13, color: 'var(--ink-700)', marginBottom: 'var(--sp-8)' }}>
          Click "Open full record page" to pivot into the nCino-grammar drill-through
          where you can triage each row.
        </p>
        <Button
          variant="primary"
          onClick={() => {
            const keys = ['sessions', 'sla-breaches', 'tasks-overdue', 'approvals-pending'];
            navigate(`drill/${keys[openKpi ?? 0]}`);
          }}
        >
          Open full record page →
        </Button>
      </KpiTileDetail>

      <OverrideDrawer open={showOverride} onClose={() => setShowOverride(false)} />
      <QuickReassignModal
        open={showReassign}
        onClose={() => setShowReassign(false)}
        onReassign={(to, reason) => {
          // TODO Wave 3: api.retail.sessions.reassign(...)
          window.console.info(`[RPM] Reassign to ${to} — ${reason}`);
        }}
      />
      <BroadcastModal open={showBroadcast} onClose={() => setShowBroadcast(false)} />
    </div>
  );
}

function relative(d: Date): string {
  const ms = Date.now() - d.getTime();
  if (ms < 5_000) return 'just now';
  if (ms < 60_000) return `${Math.floor(ms / 1000)}s ago`;
  return `${Math.floor(ms / 60_000)}m ago`;
}
